import { Component, EventEmitter, Inject, Input, OnChanges, Output } from "@angular/core";
import { QueryTrigger, SequenceTriggerAlertResultStep } from "@app2/clients/types/alert-typedefs";
import { TimeService } from "@app2/shared/services/time.service";
import { PRODUCT_DOMAIN } from "@app2/alert/alert-inbox/injection-tokens";
import { Domain } from "@app2/alert/constants";
import { UntilDestroy } from "@ngneat/until-destroy";
import { ImportantFieldData } from "@app2/search/search-shared/search-results-table.component";
import { CustomizeSearchColumnsModalComponent } from "@app2/search/search-shared/customize-search-columns-modal.component";
import { SearchClientService } from "@app2/clients/search-client.service";
import { NotificationsService } from "@app2/shared/services/notifications.service";
import { saveAs } from "file-saver";
import { ModalService } from "@app2/shared/modal/modal.service";

@UntilDestroy()
@Component({
    selector: "sequence-alert-result-step",
    template: `
        <ng-container *ngIf="productDomain === 'FRAUD'; else notFraudView">
            <h3 class="sub-header"
                test-id="alert-event-header">
                {{ "RECENT_ALERT_EVENTS" | localize }} - {{ "MATCHED_VALUES_STEPS" | localize: stepNumber }}
                <div *ngIf="numEvents > 0" class="sub-header-action-container">
                    <ds-button type="blue-link"
                               size="small"
                               (clicked)="openCustomizeSearchColumnsModal()">
                        <i class="far fa-table-columns"></i>
                        {{ "MANAGE_COLUMNS" | localize }}
                    </ds-button>
                    <span class="separator-column">|</span>
                    <ds-button type="blue-link"
                               size="small"
                               [disabled]="downloadingEvents"
                               (clicked)="downloadCsv()">
                        <i class="far" [ngClass]="downloadingEvents ? 'fa-spinner fa-spin' : 'fa-cloud-download-alt'"></i>
                        {{ "GENERIC_DOWNLOAD_CSV" | localize }}
                    </ds-button>
                </div>
            </h3>
            <events-table
                    [queryString]="stepQuery"
                    [begin]="triggeredOnMoment"
                    [end]="eomTriggeredOnMoment"
                    [filterList]="{ field: trigger.filterField, id: trigger.filterListId }"
                    [excludeList]="{ field: trigger.excludeField, id: trigger.excludeListId }"
                    [customColumns]="customColumns"
                    (onEventsFetched)="numEvents = $event"
                    (importantFieldDataChanged)="importantFieldDataChanged.emit($event)">
            </events-table>
            <div *ngIf="numEvents === 0"
                 class="info-notification"
                 test-id="info-notification">
                <i class="far fa-info-circle"></i>
                <div class="info-text-container">
                    <span class="no-events-available-text">{{ 'INBOX_NO_ALERT_EVENTS_AVAILABLE' | localize }}</span>
                    <span class="expired-events-text">{{ 'INBOX_EXPIRED_EVENTS_FOR_ALERT' | localize }}</span>
                </div>
            </div>
        </ng-container>
        <ng-template #notFraudView>
            <div *ngIf="!isFirst" class="sequence-alert-result-step-arrow">
                <svg width="104" height="12">
                    <path d="M 0 12 L 52 0 L 104 12 H 0"/>
                </svg>
            </div>
            <div class="sequence-alert-result-step-container flex-container-row">
                <div class="step-title">
                    {{ 'SEQUENCE_QUERY_ALERT_RESULT_STEP_NUMBER' | localize: stepNumber }}
                </div>
                <div class="step-query">
                    <search-link [queryString]="stepQuery"
                                 [begin]="resultStep.triggeredOn"
                                 [end]="eomTriggeredOn"
                                 [newTab]="true"
                                 [defaultText]="false">
                        {{ query }}
                    </search-link>
                </div>
                <div class="step-trigger-time">
                    {{ 'SEQUENCE_QUERY_ALERT_RESULT_TRIGGER_TIME' | localize }} {{ resultStep.triggeredOn | shortDate }}
                </div>
                <div class="medium-16 small-16 left-text">
                    <expand-events
                            [queryString]="stepQuery"
                            [begin]="triggeredOnMoment"
                            [end]="eomTriggeredOnMoment"
                            [showText]="'SHOW_STEP_RESULTS'"
                            [hideText]="'HIDE_STEP_RESULTS'">
                    </expand-events>
                </div>
            </div>

            <div *ngIf="!isLast" class="sequence-alert-result-step-arrow">
                <svg width="104" height="12">
                    <path d="M 0 0 L 52 12 L 104 0 H 0"/>
                </svg>
            </div>
            <div *ngIf="!isLast" class="sequence-alert-result-matches">
                <div class="matches-title">{{ 'SEQUENCE_QUERY_ALERT_RESULT_MATCHES' | localize }}</div>
                <div *ngFor="let match of resultStep.matchValues"
                     class="sequence-alert-step-match-entry flex-container-row">
                    <div class="match-key">
                        {{ match.outputKey === match.inputKey
                            ? match.outputKey
                            : match.outputKey + ", " + match.inputKey }}
                    </div>
                    <div class="match-key-value-split">=</div>
                    <div class="match-value">
                        <search-link [queryString]="combinedQuery"
                                     [begin]="resultStep.triggeredOn"
                                     [end]="eomCombinedEnd"
                                     [newTab]="true"
                                     [defaultText]="false">
                            {{ match.value }}
                        </search-link>
                        <a *ngIf="showAlertStoryLink"
                           class="link-text no-underline story-view-link"
                           dsRouterLink="."
                           [queryParams]="{ mode: 'alert_story', selected: match.value }"
                           queryParamsHandling="merge"
                           test-class="view-alert-story">
                            <i class="fa fa-list show-on-hover"
                               [matTooltip]="'INBOX_VIEW_STORY' | localize"></i>
                        </a>
                    </div>
                    <div class="medium-16 small-16 left-text">
                        <expand-events
                                [queryString]="combinedQuery"
                                [begin]="triggeredOnMoment"
                                [end]="eomCombinedEndMoment"
                                [showText]="'SHOW_STEP_RESULTS'"
                                [hideText]="'HIDE_STEP_RESULTS'">
                        </expand-events>
                    </div>
                </div>
            </div>
        </ng-template>
    `,
    styles: [`
        @import "/src/styles/colors";

        .sub-header-action-container {
            float: right;

            .separator-column {
                color: $gray-5;
                fonts-size: 0.875rem;
                font-weight: normal;
                line-height: 1rem;
                margin: 0 16px;
            }
        }
    `],
})
export class SequenceAlertResultStepComponent implements OnChanges {
    @Input() query: string;
    @Input() resultStep: SequenceTriggerAlertResultStep;
    @Input() stepNumber: number;
    @Input() stepQuery: string;
    @Input() combinedQuery: string;
    @Input() combinedEnd: string;
    @Input() isFirst: boolean;
    @Input() isLast: boolean;
    @Input() showAlertStoryLink: boolean = false;
    @Input() customColumns: string[];
    @Input() trigger: QueryTrigger;

    @Output() importantFieldDataChanged = new EventEmitter<ImportantFieldData[]>();

    eomCombinedEnd: string;
    eomTriggeredOn: string;
    triggeredOnMoment: Moment;
    eomCombinedEndMoment: Moment;
    eomTriggeredOnMoment: Moment;
    numEvents: number;
    downloadingEvents: boolean = false;

    constructor(@Inject(PRODUCT_DOMAIN) public productDomain: Domain,
                private timeService: TimeService,
                private searchClient: SearchClientService,
                private notificationsService: NotificationsService,
                private modalService: ModalService) {
    }

    ngOnChanges() {
        this.eomCombinedEndMoment = this.endOfMinute(this.combinedEnd);
        this.triggeredOnMoment = this.timeService.timeZoneMoment(this.resultStep.triggeredOn);
        this.eomTriggeredOnMoment = this.endOfMinute(this.resultStep.triggeredOn);

        this.eomCombinedEnd = this.eomCombinedEndMoment.toISOString();
        this.eomTriggeredOn = this.eomTriggeredOnMoment.toISOString();
    }

    private endOfMinute(timestamp: string): Moment {
        return moment(timestamp).add(59, "seconds");
    }

    downloadCsv() {
        const request = {
            queryString: this.stepQuery,
            begin: this.triggeredOnMoment,
            end: this.eomTriggeredOnMoment,
            pageSize: 50_000,
        };
        this.downloadingEvents = true;
        this.searchClient.downloadCsv(request)
            .then(blob => saveAs(blob, `DefenseStorm-AlertEvents-${ this.timeService.now().format(this.timeService.formats.timestampDate) }-${ this.trigger.name }.csv`))
            .catch(error => this.notificationsService.showError("CSV_DOWNLOAD_ERROR", error))
            .finally(() => this.downloadingEvents = false);
    }

    openCustomizeSearchColumnsModal(): void {
        this.modalService
            .open(CustomizeSearchColumnsModalComponent, {
                customColumns: this.customColumns,
            })
            .afterClosedWithResult()
            .subscribe(customColumns => this.customColumns = customColumns);
    }
}
